import React from 'react';

import ContentCarouselButton from '@/components/atoms/Button/ContentCarouselButton/CardCarouselButton';
import R2Image from '@/components/ui/R2Image';
import RichText from '@/components/ui/RichText/RichText';
import { COMPONENT_STYLES } from '@/constants/styles';

interface CarouselItemProps {
  item: any;
  idx: number; // Add this
  hoveredIndex: number | null; // Add this
  isActive: boolean;
  isDimmed: boolean;
  isMobile: boolean;
  onSelect: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onButtonSelect: () => void; // New prop
  innerRef: (el: HTMLDivElement | null) => void;
}

const CarouselItem: React.FC<CarouselItemProps> = ({
  item,
  idx,
  hoveredIndex,
  isActive,
  isDimmed,
  isMobile,
  onSelect,
  onMouseEnter,
  onMouseLeave,
  innerRef,
  onButtonSelect,
}) => {
  const disableInteraction = isMobile && !isActive;

  const containerClasses = [
    'relative rounded-[10px] overflow-hidden transition-all duration-300 ease-out',
    'w-60 md:w-75 h-[360px] lg:h-[420px] shrink-0 focus:outline-none',
    disableInteraction ? 'pointer-events-none' : 'cursor-pointer',

    // SCALE LOGIC:
    isActive
      ? isDimmed
        ? 'scale-[0.95] z-0'
        : 'scale-[1.05] z-10'
      : hoveredIndex === idx
        ? 'scale-[1.05] z-10'
        : 'scale-[0.95] z-0',
  ].join(' ');
  const title = item?.title || item?.heading?.content || 'Concern';
  const imagePath =
    item?.image?.url ||
    item?.image?.urlDesktop ||
    item?.image?.urlMobile ||
    item?.imageUrl ||
    '';
  const imageAlt = item?.image?.alt || item?.image?.title || title;
  const buttonTitle = item?.button?.content || 'View Service';
  const slug = item?.slug || item?.image?.slug || '/treatments';
  const functionality = item?.functionality || 'internal_link';

  return (
    <div
      ref={innerRef}
      onClick={disableInteraction ? undefined : onSelect}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      tabIndex={disableInteraction ? -1 : 0}
      className={containerClasses}
    >
      {imagePath ? (
        <R2Image
          path={imagePath}
          alt={imageAlt}
          width={300}
          height={400}
          // Card is w-60 (240px × h-360px) below md, w-75 (300px × h-420px)
          // at md+. With object-cover on a 3:2 photo the actual rendered
          // content width = card height × 1.5 (540 / 630 px), not the card's
          // CSS layout width. Without this correction the browser downloads a
          // ~320w variant that gets stretched >2× on 1x screens → blurry.
          sizes="(max-width: 767px) 540px, 630px"
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center rounded-lg bg-gray-200">
          <span className="text-sm text-gray-500">No image available</span>
        </div>
      )}
      <div
        className={`${COMPONENT_STYLES.CARD.GLASS} absolute right-3 bottom-3 left-3 m-2 rounded-lg p-4 text-left text-white lg:m-1`}
      >
        <RichText styles="text-[18px] font-bold">{title}</RichText>
        <span
          onClickCapture={(e) => {
            e.stopPropagation();
            onButtonSelect(); // Use this instead of onSelect
          }}
        >
          <ContentCarouselButton
            data={{ title: buttonTitle, slug, functionality }}
            className="mt-2"
          />
        </span>
      </div>
    </div>
  );
};

export default React.memo(CarouselItem);
